import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useId } from "react";
import { FormattedMessage } from "react-intl";

import {
  MaplibreLegend,
  MaplibreLegendLayer,
  RanaButton,
  RanaHeading,
  RanaLoading,
  TrashIcon,
} from "@nens/ranalib";
import { EMPTY_LAYER, EmptyLayers } from "@nens/react-map-gl/components";

import { LayerControl, LayerControlHeader } from "@/components/layer-control";
import { useClickedOnVectorMap } from "@/hooks/use-clicked-on-vector-map";
import DeadFrog from "@/images/deadFrog.png";
import { BUILDING_LAYER_ID, RanaMap } from "@/lib/rana-map";
import { RanaMapDisplayVectors } from "@/lib/rana-map/components/rana-map-vectors";
import { DisplayPopupInfo } from "@/lib/rana-popup/components/display-popup-info";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";
import { RanaCogRasterColorSettings } from "@/lib/rana-raster-details/components/rana-raster-color-settings";
import { ProjectFile } from "@/types/api-model-simple-names";
import { SchematisationFileDescriptor } from "@/types/descriptor-types";

import { EXTENT_LAYER_ID, useSchematisationPreview } from "../hooks/use-schematisation-preview";
interface ProjectFilePreviewSchematisationProps {
  file: ProjectFile;
  descriptor: SchematisationFileDescriptor;
  title: string;
}

export function ProjectFilePreviewSchematisation({
  file,
  descriptor,
  title,
}: ProjectFilePreviewSchematisationProps) {
  const mapId = `schema-map-${useId()}`;

  const {
    isLoading,
    isFetchingSchematisationExistence,
    bounds,
    vectorLayers,
    visibleLayers,
    setVisibleLayers,
    schematisationHasBeenDeleted,
    isMutating,
    deleteFile,
    rasterLayers,
    setRasterColorRange,
    setRasterColorBounds,
    setRasterDefaultColor,
    layers,
    sprites,
  } = useSchematisationPreview({
    file,
    descriptor,
  });

  const { clearMapClick, point, clickedPoint, setPoint, vectorInfoItems } = useClickedOnVectorMap(
    mapId,
    vectorLayers
  );

  const popupItems = vectorInfoItems.map((item) => ({ ...item, isVector: true as const }));

  const legendLayers = vectorLayers
    .map(({ layer, styleLayers, type }) => ({ layer, styleLayers, type }))
    .filter(
      (l): l is MaplibreLegendLayer =>
        visibleLayers.some((visibleLayer) => visibleLayer.layer === l.layer) &&
        l.styleLayers !== undefined
    );

  if (isFetchingSchematisationExistence) return <RanaLoading size="xl" color="brand.clear_creek" />;

  if (schematisationHasBeenDeleted) {
    return (
      <Flex textAlign="center" flexDir="column" alignItems="center" gap={4}>
        <Image objectFit="cover" h={44} src={DeadFrog} />
        <RanaHeading type="h3">
          <FormattedMessage
            id="projectFile.schematisation404.title"
            defaultMessage="This model schematisation no longer exists"
          />
        </RanaHeading>
        <Text>
          <FormattedMessage
            id="projectFile.schematisation404.description"
            defaultMessage="The corresponding instance on Rana HCC was removed. This reference should be deleted"
          />
        </Text>
        <RanaButton
          w="fit"
          isLoading={isMutating}
          variant="primary"
          leftIcon={<TrashIcon />}
          onClick={deleteFile}
        >
          <FormattedMessage id="projectFile.deleteReference" defaultMessage="Delete" />
        </RanaButton>
      </Flex>
    );
  }

  if (!bounds)
    return (
      <FormattedMessage
        id="schematisation.model_extent_not_available"
        defaultMessage="Model extent not available"
      />
    );

  const hasLayers = layers.length > 0;
  const hasVectorLayers = vectorLayers.length > 0;
  const hasExtent = layers.includes(EXTENT_LAYER_ID);

  const getBeforeId = (layerName: string) => {
    const layerIndex = layers.findIndex((layer) => layer === layerName);
    return layerIndex === -1 ? BUILDING_LAYER_ID : EMPTY_LAYER + (99 - layerIndex);
  };

  const visibleLayerSet = new Set(visibleLayers.map((visibleLayer) => visibleLayer.layer));
  const rasterControlLayers = rasterLayers.filter(
    (layer) =>
      !layer.isHillshade && visibleLayerSet.has(layer.name) && (layer.colorStops?.length ?? 0) > 1
  );
  const hasBottomLeftLegends = legendLayers.length > 0 || rasterControlLayers.length > 0;

  return (
    <RanaMap isLoading={isLoading} mapId={mapId} bounds={bounds} mapProps={{}} sprites={sprites}>
      {/* Reserve deterministic anchor layers so all map layers can use beforeId and keep the correct order regardless of load timing. */}
      <EmptyLayers beforeId={BUILDING_LAYER_ID} />
      <RanaMap.MapScale />

      {/* CONTROLS */}
      {hasLayers && (
        <RanaMap.ControlsTopLeft>
          <LayerControlHeader />
          <LayerControl
            descriptorId={descriptor.id}
            title={title}
            layers={layers}
            visibleLayers={visibleLayers}
            setVisibleLayers={setVisibleLayers}
          />
        </RanaMap.ControlsTopLeft>
      )}
      {hasBottomLeftLegends && (
        <RanaMap.ControlsBottomLeft>
          <Box w="xs" p={4} maxH="2xs" overflowY="auto">
            {legendLayers.length > 0 && <MaplibreLegend layers={legendLayers} mapId={mapId} />}
            {rasterControlLayers.map((rasterLayer) => (
              <RanaCogRasterColorSettings
                key={`legend-${rasterLayer.id}`}
                label={rasterLayer.name}
                legendId={`${mapId}-${rasterLayer.id}-legend`}
                legend={rasterLayer.colorStops}
                colorRange={rasterLayer.colorRange}
                defaultColorRange={rasterLayer.defaultColorRange}
                setColorRange={(range) => setRasterColorRange(rasterLayer.id, range)}
                setColorBounds={(rasterBounds) =>
                  setRasterColorBounds(rasterLayer.id, rasterBounds)
                }
                setDefaultColor={() => setRasterDefaultColor(rasterLayer.id)}
              />
            ))}
          </Box>
        </RanaMap.ControlsBottomLeft>
      )}

      {hasVectorLayers && (
        <>
          {/* DISPLAY VECTOR LAYERS */}
          <RanaMapDisplayVectors
            layers={vectorLayers.map((fullLayer) => {
              const isVisible = visibleLayers.some(
                (visibleLayer) => visibleLayer.layer === fullLayer.layer
              );
              return {
                ...fullLayer,
                isVisible,
                beforeId: getBeforeId(fullLayer.layer),
              };
            })}
          />

          {/* VECTOR POPUP */}
          {vectorInfoItems.length > 0 && point && (
            <RanaPopup anchor="bottom" point={point} onClose={clearMapClick}>
              <DisplayPopupInfo
                items={popupItems}
                setPoint={setPoint}
                clickedPoint={clickedPoint}
                isCompact
              />
            </RanaPopup>
          )}
        </>
      )}

      {rasterLayers.map((layer) => (
        <RanaMap.CogLayer
          key={layer.id}
          {...layer}
          beforeId={getBeforeId(layer.name)}
          bounds={bounds}
          isVisible={visibleLayerSet.has(layer.name)}
        />
      ))}

      {/* If there are no layers the model extent will be shown. */}
      {hasExtent && (
        <RanaMap.ExtentLayer
          isVisible={!!visibleLayers.find(({ layer }) => layer === EXTENT_LAYER_ID)}
          bounds={bounds}
        />
      )}
    </RanaMap>
  );
}
